import { LOCALE_ID, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';


/* TRANSLATE */
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ASSET_VERSION } from './shared/utils/asset-version';

/* DatePipe */
import { DatePipe, registerLocaleData } from '@angular/common';

// GLOBALS
import { Globals } from '../app/shared/globals/globals';

/* CHART JS */
import { NgChartsModule } from 'ng2-charts';

/* INTERCEPTORS */
/* import { ErrorsHandler } from './shared/services/interceptors/errors-handler'; */
import { HttpsInterceptor } from './shared/services/interceptors/http-interceptor';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
    const suffix = `.json?v=${ASSET_VERSION}`;
    return new TranslateHttpLoader(http, 'assets/i18n/', suffix);
}

// LOCALE
import localeIt from '@angular/common/locales/it';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
registerLocaleData(localeIt);
registerLocaleData(localeEn);
registerLocaleData(localeDe);

// MATERIAL
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatButtonToggleModule } from '@angular/material/button-toggle';



// SERVICES
import { TranslateTeiasoftService } from "./shared/services/utils/translate-teiasoft.service";
import { UtilsService } from './shared/services/utils/utils.service';
import { TeiasoftLoaderService } from './shared/services/teiasoft-bucket/teiasoft-loader.service';
import { CookiesService } from './shared/services/utils/cookies.service';
import { BackendService } from './shared/services/backend/backend.service';
import { TeiasoftOdataService } from './shared/services/teiasoft-bucket/teiasoft-odata.service';
import { TeiasoftGridService } from './shared/services/teiasoft-bucket/teiasoft-grid.service';

// PLUGINS
import { TeiasoftLoaderComponent } from './shared/teiasoft-bucket/teiasoft-loader/teiasoft-loader.component';
import { TeiasoftLinkComponent } from './shared/teiasoft-bucket/teiasoft-link/teiasoft-link.component';
import { TeiasoftToastComponent } from './shared/teiasoft-bucket/teiasoft-toast/teiasoft-toast.component';
import { TeiasoftButtonComponent } from './shared/teiasoft-bucket/teiasoft-button/teiasoft-button.component';
import { TeiasoftModalComponent } from './shared/teiasoft-bucket/teiasoft-modal/teiasoft-modal.component';
import { TeiasoftInputComponent } from './shared/teiasoft-bucket/teiasoft-input/teiasoft-input.component';
import { TeiasoftHeaderComponent } from './shared/teiasoft-bucket/teiasoft-header/teiasoft-header.component';
import { TeiasoftLoginComponent } from './shared/teiasoft-bucket/teiasoft-login/teiasoft-login.component';
import { TeiasoftTitleComponent } from './shared/teiasoft-bucket/teiasoft-title/teiasoft-title.component';
import { TeiasoftTileComponent } from './shared/teiasoft-bucket/teiasoft-tile/teiasoft-tile.component';
import { TeiasoftMenuComponent } from './shared/teiasoft-bucket/teiasoft-menu/teiasoft-menu.component';
import { TeiasoftObjectComponent } from './shared/teiasoft-bucket/teiasoft-object/teiasoft-object.component';
import { TeiasoftTextareaComponent } from './shared/teiasoft-bucket/teiasoft-textarea/teiasoft-textarea.component';
import { TeiasoftGridComponent } from './shared/teiasoft-bucket/teiasoft-grid/teiasoft-grid.component';
import { TeiasoftSelectComponent } from './shared/teiasoft-bucket/teiasoft-select/teiasoft-select.component';
import { TeiasoftGridSettingsComponent } from './shared/teiasoft-bucket/teiasoft-grid-odata/teiasoft-grid-settings/teiasoft-grid-settings.component';

/* COMPONENTS */
import { LoginComponent } from './login/login.component';
import { EnquiryBookComponent } from './internal/enquiry-book/enquiry-book.component';
import { TeiasoftToolbarComponent } from './shared/teiasoft-bucket/teiasoft-toolbar/teiasoft-toolbar.component';
import { TeiasoftDialogComponent } from './shared/teiasoft-bucket/teiasoft-dialog/teiasoft-dialog.component';
import { TeiasoftDatetimeComponent } from './shared/teiasoft-bucket/teiasoft-datetime/teiasoft-datetime.component';
import { TeiasoftWeekPickerComponent } from './shared/teiasoft-bucket/teiasoft-week-picker/teiasoft-week-picker.component';
import { TeiasoftEmptyfieldComponent } from './shared/teiasoft-bucket/teiasoft-emptyfield/teiasoft-emptyfield.component';
import { TeiasoftListComponent } from './shared/teiasoft-bucket/teiasoft-list/teiasoft-list.component';
import { ModalSelectCustomerComponent } from './shared/components/modal-select-customer/modal-select-customer.component';
import { CreateEditDummyComponent } from './shared/components/create-edit-dummy/create-edit-dummy.component';
import { TeiasoftGridOdataComponent } from './shared/teiasoft-bucket/teiasoft-grid-odata/teiasoft-grid-odata.component';
import { TeiasoftToggleComponent } from './shared/teiasoft-bucket/teiasoft-toggle/teiasoft-toggle.component';
import { ModalSelectNpsComponent } from './shared/components/modal-select-nps/modal-select-nps.component';
import { EditEnquiryComponent } from './internal/enquiry-book/edit-enquiry/edit-enquiry.component';
import { TeiasoftChipsComponent } from './shared/teiasoft-bucket/teiasoft-chips/teiasoft-chips.component';
import { DuplicateEnquiryResultComponent } from './internal/enquiry-book/duplicate-enquiry-result/duplicate-enquiry-result.component';
import { CloseEnquiryComponent } from './internal/enquiry-book/close-enquiry/close-enquiry.component';
import { HistoryComponent } from './internal/history/history.component';
import { LogsComponent } from './internal/logs/logs.component';
import { ModalItemComponent } from './internal/enquiry-book/modal-item/modal-item.component';
import { TeiasoftBreadcrumbComponent } from './shared/teiasoft-bucket/teiasoft-breadcrumb/teiasoft-breadcrumb.component';
import { ModalAttachComponent } from './internal/enquiry-book/modal-attach/modal-attach.component';
import { ModalEditinplaceComponent } from './internal/enquiry-book/edit-enquiry/modal-editinplace/modal-editinplace.component';
import { TeiasoftDatasheetComponent } from './shared/teiasoft-bucket/teiasoft-datasheet/teiasoft-datasheet.component';
import { ModalAttachmentsComponent } from './internal/enquiry-book/modal-attachments/modal-attachments.component';
import { ModalObjectComponent } from './shared/teiasoft-bucket/teiasoft-datasheet/modal-object/modal-object.component';
import { HomeComponent } from './internal/home/home.component';
import { ChangeStatusComponent } from './internal/enquiry-book/change-status/change-status.component';
import { FormEnquiryComponent } from './internal/home/form-enquiry/form-enquiry.component';
import { ResizableColumnDirective } from './shared/resizable-column.directive';
import { TeiasoftConfirmableInputComponent } from './shared/teiasoft-bucket/teiasoft-confirmable-input/teiasoft-confirmable-input.component';
import { AbsPipe } from './abs.pipe';
import { UsersComponent } from './internal/manage-users/users/users.component';
import { ModalUserComponent } from './internal/manage-users/modal-user/modal-user.component';
import { GroupsComponent } from './internal/manage-groups/groups/groups.component';
import { ModalGroupComponent } from './internal/manage-groups/modal-group/modal-group.component';
import { TeiasoftOdataFiltersComponent } from './shared/components/teiasoft-odata-filters/teiasoft-odata-filters.component';
import { TeiasoftSelectOptionsComponent } from './shared/teiasoft-bucket/teiasoft-select-options/teiasoft-select-options.component';
import { ModalFeasibilityOptionsComponent } from './shared/components/modal-feasibility-options/modal-feasibility-options.component';
import { DocumentTemplateComponent } from './internal/enquiry-book/document-template/document-template.component';
import { HeaderSectionComponent } from './internal/enquiry-book/document-template/document-sections/header-section/header-section.component';
import { OtherInformationSectionComponent } from './internal/enquiry-book/document-template/document-sections/other-information-section/other-information-section.component';
import { DeliverySectionComponent } from './internal/enquiry-book/document-template/document-sections/delivery-section/delivery-section.component';
import { PaymentSectionComponent } from './internal/enquiry-book/document-template/document-sections/payment-section/payment-section.component';
import { CustomerSpecialRequirementsSectionComponent } from './internal/enquiry-book/document-template/document-sections/customer-special-requirements-section/customer-special-requirements-section.component';

/* TEXT EDITOR */
import { NgxEditorModule } from 'ngx-editor';
import { GenericItemsSectionComponent } from './internal/enquiry-book/document-template/document-sections/generic-items-section/generic-items-section.component';
import { TechnicalDescriptionSectionComponent } from './internal/enquiry-book/document-template/document-sections/technical-description-section/technical-description-section.component';
import { ImportQuotationComponent } from './internal/enquiry-book/document-template/import-quotation/import-quotation.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FinalSectionComponent } from './internal/enquiry-book/document-template/document-sections/final-section/final-section.component';
import { ModalPriceOptionsComponent } from './shared/components/modal-price-options/modal-price-options.component';
import { ModalPriceOptionsCrComponent } from './shared/components/modal-price-options/modal-price-options-cr.component';
import { CostingToolComponent } from './internal/costing-tool/costing-tool.component';
import { DocumentTemplateOptionsComponent } from './internal/enquiry-book/document-template/document-template-options/document-template-options.component';
import { ModalExtraCostsComponent } from './internal/enquiry-book/edit-enquiry/modal-extra-costs/modal-extra-costs.component';
import { ViewMasterdataComponent } from './internal/view-masterdata/view-masterdata.component';
import { ModalEditinplaceCostsComponent } from './internal/enquiry-book/edit-enquiry/modal-editinplace-costs/modal-editinplace-costs.component';
import { ModalAddExtraCostsComponent } from './internal/enquiry-book/edit-enquiry/modal-editinplace-costs/modal-add-extra-costs/modal-add-extra-costs.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ImportMasterdataComponent } from './internal/import-masterdata/import-masterdata.component';
import { ModalItemDeliveryDatesComponent } from './internal/enquiry-book/edit-enquiry/modal-item-delivery-dates/modal-item-delivery-dates.component';
import { ModalDeliveryOptionsComponent } from './shared/components/modal-delivery-options/modal-delivery-options.component';
import { ModalTechnicalDescriptionComponent } from './internal/enquiry-book/document-template/document-sections/generic-items-section/modal-technical-description/modal-technical-description.component';
import { ModalIncreaseColumnComponent } from './internal/enquiry-book/edit-enquiry/modal-editinplace-costs/modal-increase-column/modal-increase-column.component';
import { ModalUploadItemsIAComponent } from './internal/enquiry-book/edit-enquiry/modal-upload-items-ia/modal-upload-items-ia.component';
import { ModalCostingToolComponent } from './internal/enquiry-book/edit-enquiry/modal-costing-tool/modal-costing-tool.component';
import { ModalSimilarEnquiriesComponent } from './internal/enquiry-book/modal-similar-enquiries/modal-similar-enquiries.component';
import { ModalAddOrderExtraCostsComponent } from './internal/enquiry-book/edit-enquiry/modal-editinplace-costs/modal-add-order-extra-costs/modal-add-order-extra-costs.component';
import { EnquiryBookPricingCommitteeComponent } from './internal/enquiry-book-pricing-committee/enquiry-book-pricing-committee.component';
import { ModalItdcrComponent } from './internal/enquiry-book/modal-itdcr/modal-itdcr.component';
import { ItdcrComponent } from './internal/itdcr/itdcr.component';
import { AngularSplitModule } from 'angular-split';
import { ModalSendItdcrComponent } from './internal/itdcr/modal-send-itdcr/modal-send-itdcr.component';
import { ModalItdcrAttachmentsComponent } from "./internal/itdcr/modal-itdcr-attachments/modal-itdcr-attachments.component";
import { ModalItdcrVisualOverviewComponent } from './internal/itdcr/modal-itdcr-visual-overview/modal-itdcr-visual-overview.component';
import { ModalViewItdcrComponent } from './internal/itdcr/modal-view-itdcr/modal-view-itdcr.component';
import { MatButtonModule } from '@angular/material/button';
import { ModalItdcrNotesComponent } from './internal/itdcr/modal-itdcr-notes/modal-itdcr-notes.component';
import { ModalSpecialRequirementsComponent } from './internal/enquiry-book/modal-special-requirements/modal-special-requirements.component';
import { ModalRevisionComponent } from './internal/enquiry-book/modal-revision/modal-revision.component';
import { ModalPriceMarginChangesComponent } from './internal/enquiry-book/modal-price-margin-changes/modal-price-margin-changes.component';
import { ModalFeasibilityOptionsItdcrComponent } from './shared/components/modal-feasibility-options-itdcr/modal-feasibility-options-itdcr.component';
import { DocumentEditorMenuComponent } from './shared/ngx-editor/document-editor-menu/document-editor-menu.component';


@NgModule({
    declarations: [
        AppComponent,
        TeiasoftLoaderComponent,
        TeiasoftLinkComponent,
        TeiasoftToastComponent,
        TeiasoftButtonComponent,
        TeiasoftModalComponent,
        TeiasoftInputComponent,
        TeiasoftHeaderComponent,
        TeiasoftLoginComponent,
        TeiasoftTitleComponent,
        TeiasoftTileComponent,
        TeiasoftMenuComponent,
        TeiasoftObjectComponent,
        TeiasoftGridComponent,
        LoginComponent,
        TeiasoftTextareaComponent,
        TeiasoftSelectComponent,
        EnquiryBookComponent,
        EnquiryBookPricingCommitteeComponent,
        TeiasoftToolbarComponent,
        TeiasoftDialogComponent,
        TeiasoftDatetimeComponent,
        TeiasoftWeekPickerComponent,
        TeiasoftEmptyfieldComponent,
        TeiasoftListComponent,
        TeiasoftGridSettingsComponent,
        ModalSelectCustomerComponent,
        CreateEditDummyComponent,
        TeiasoftGridOdataComponent,
        TeiasoftToggleComponent,
        ModalSelectNpsComponent,
        EditEnquiryComponent,
        TeiasoftChipsComponent,
        DuplicateEnquiryResultComponent,
        CloseEnquiryComponent,
        HistoryComponent,
        LogsComponent,
        ModalItemComponent,
        TeiasoftBreadcrumbComponent,
        ModalAttachComponent,
        ModalEditinplaceComponent,
        TeiasoftDatasheetComponent,
        ModalAttachmentsComponent,
        ModalObjectComponent,
        HomeComponent,
        ChangeStatusComponent,
        FormEnquiryComponent,
        ResizableColumnDirective,
        TeiasoftConfirmableInputComponent,
        AbsPipe,
        UsersComponent,
        ModalUserComponent,
        GroupsComponent,
        ModalGroupComponent,
        TeiasoftOdataFiltersComponent,
        TeiasoftSelectOptionsComponent,
        ModalFeasibilityOptionsComponent,
        ModalFeasibilityOptionsItdcrComponent,
        DocumentTemplateComponent,
        HeaderSectionComponent,
        CustomerSpecialRequirementsSectionComponent,
        OtherInformationSectionComponent,
        DeliverySectionComponent,
        PaymentSectionComponent,
        GenericItemsSectionComponent,
        TechnicalDescriptionSectionComponent,
        ImportQuotationComponent,
        FinalSectionComponent,
        ModalPriceOptionsComponent,
        ModalPriceOptionsCrComponent,
        CostingToolComponent,
        DocumentTemplateOptionsComponent,
        ModalExtraCostsComponent,
        ViewMasterdataComponent,
        ModalEditinplaceCostsComponent,
        ModalAddExtraCostsComponent,
        ImportMasterdataComponent,
        ModalItemDeliveryDatesComponent,
        ModalDeliveryOptionsComponent,
        ModalTechnicalDescriptionComponent,
        ModalIncreaseColumnComponent,
        ModalUploadItemsIAComponent,
        ModalCostingToolComponent,
        ModalSimilarEnquiriesComponent,
        ModalAddOrderExtraCostsComponent,
        ModalItdcrComponent,
        ItdcrComponent,
        ModalSendItdcrComponent,
        ModalViewItdcrComponent,
        ModalItdcrAttachmentsComponent,
        ModalItdcrVisualOverviewComponent,
        ModalItdcrNotesComponent,
        ModalSpecialRequirementsComponent,
        ModalRevisionComponent,
        ModalPriceMarginChangesComponent,
        DocumentEditorMenuComponent
    ],
    bootstrap: [AppComponent],
    imports: [BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserModule,
        AppRoutingModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        AngularSplitModule,
        NgChartsModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatNativeDateModule,
        MatInputModule,
        MatMenuModule,
        MatTooltipModule,
        MatExpansionModule,
        MatDialogModule,
        NgxEditorModule,
        ScrollingModule,
        DragDropModule,
        MatProgressBarModule,
        MatCheckboxModule,
        MatExpansionModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        MatIconModule,
        MatChipsModule,
        MatListModule,
        MatButtonToggleModule
    ],
    providers: [Globals, UtilsService, TeiasoftLoaderService, TranslateTeiasoftService, CookiesService, BackendService,
        DatePipe, TeiasoftOdataService, TeiasoftGridService,
        provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
        { provide: HTTP_INTERCEPTORS, useClass: HttpsInterceptor, multi: true },
        { provide: LOCALE_ID, useValue: 'it-IT' },
        { provide: LOCALE_ID, useValue: 'en-EN' },
        { provide: LOCALE_ID, useValue: 'de-DE' }, provideHttpClient(withInterceptorsFromDi()),]
})
export class AppModule { }
